import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getShopById, getHomeServices, createHomeOrder, confirmHomeBooking } from "../utils/api";
import { useAuth } from "../context/AuthContext";
import Toast from "../components/Toast";

export default function HomeBookingPage() {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [shop, setShop] = useState(null);
  const [homeServices, setHomeServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [address, setAddress] = useState("");
  const [preferredDate, setPreferredDate] = useState("");
  const [preferredTime, setPreferredTime] = useState("");
  const [note, setNote] = useState("");
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [booking, setBooking] = useState(null);
  const [waOpened, setWaOpened] = useState(false);
  const [toast, setToast] = useState({ message: "", type: "success" });

  const showToast = (message, type = "success") => setToast({ message, type });

  const timeSlots = [
    "Morning (9 AM – 12 PM)",
    "Afternoon (12 PM – 4 PM)",
    "Evening (4 PM – 8 PM)",
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [shopRes, servicesRes] = await Promise.all([
          getShopById(shopId),
          getHomeServices(shopId),
        ]);
        setShop(shopRes.data.shop || shopRes.data);
        const data = servicesRes.data;
        setHomeServices(Array.isArray(data.services) ? data.services : Array.isArray(data) ? data : []);
      } catch {
        showToast("Could not load shop details.", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [shopId]);

  const toggleService = (service) => {
    setSelectedServices((prev) =>
      prev.find((s) => s.id === service.id)
        ? prev.filter((s) => s.id !== service.id)
        : [...prev, service]
    );
  };

  const totalPrice = selectedServices.reduce((sum, s) => sum + parseFloat(s.price), 0);
  const totalAdvance = selectedServices.reduce((sum, s) => sum + parseFloat(s.advance_amount), 0);
  const totalDuration = selectedServices.reduce((sum, s) => sum + parseInt(s.duration), 0);

  const getTodayDate = () => new Date().toISOString().split("T")[0];

  const loadRazorpay = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
    });
 };

  const handlePayment = async () => {
  if (!address.trim()) return showToast("Please enter your address.", "warning");
  if (!preferredDate) return showToast("Please select a preferred date.", "warning");
  if (!preferredTime) return showToast("Please select a preferred time.", "warning");

  setPaying(true);
  try {
    const loaded = await loadRazorpay();
    if (!loaded) {
      showToast("Payment system failed to load. Try again.", "error");
      setPaying(false);
      return;
    }

    const orderRes = await createHomeOrder({
      shop_id: shopId,
      service_ids: selectedServices.map((s) => s.id),
      service_names: selectedServices.map((s) => s.name).join(", "),
      advance_amount: totalAdvance,
    });

    const { order_id, amount } = orderRes.data;

    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID,
      amount,
      currency: "INR",
      name: "TrimSpot",
      description: `Home visit advance — ${shop?.name}`,
      order_id,
      handler: async (response) => {
  try {
    console.log("RZP response:", response);
    const confirmRes = await confirmHomeBooking({
      shop_id: parseInt(shopId),
      service_ids: selectedServices.map((s) => Number(s.id)),
      service_names: selectedServices.map((s) => s.name).join(", "),
      total_price: totalPrice,
      advance_amount: totalAdvance,
      customer_address: address,
      preferred_date: preferredDate,
      preferred_time: preferredTime,
      customer_note: note,
      razorpay_order_id: response.razorpay_order_id,
      razorpay_payment_id: response.razorpay_payment_id,
      razorpay_signature: response.razorpay_signature,
    });
    setBooking(confirmRes.data.booking);
    setStep(4);
    } catch (err) {
        console.error("Confirm error:", err.response?.data || err.message);
        showToast(err.response?.data?.message || "Booking failed. Contact support.", "error");
    }
    },
      prefill: { name: user?.name, contact: user?.phone },
      theme: { color: "#e94560" },
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  } catch {
    showToast("Could not initiate payment. Try again.", "error");
  } finally {
    setPaying(false);
  }
};

  const handleWhatsApp = (isAuto = false) => {
    const phone = shop?.phone?.replace(/[^0-9]/g, "");
    if (!phone) return showToast("Shop has no phone number listed.", "warning");

    if (!isAuto && waOpened) {
      showToast("You have already notified the salon!", "success");
      return;
    }
    if (!isAuto && !waOpened) {
      showToast("Notifying salon now!", "info");
    }

    const serviceNames = selectedServices.map((s) => s.name).join(", ");
    const message =
      `Hello! I have booked a *home visit* through TrimSpot.\n\n` +
      `👤 Customer: *${user?.name}*\n` +
      `📞 Phone: *${user?.phone}*\n` +
      `🏠 Address: *${address}*\n` +
      `💈 Services: *${serviceNames}*\n` +
      `⏱ Duration: *${totalDuration} mins*\n` +
      `📅 Preferred Date: *${preferredDate}*\n` +
      `🕐 Preferred Time: *${preferredTime}*\n` +
      `💰 Total Amount: *₹${totalPrice}*\n` +
      `💳 Advance Paid: *₹${totalAdvance}*\n` +
      `📝 Note: *${note || "None"}*\n\n` +
      `Please confirm the visit. Thank you! 🙏`;

    setWaOpened(true);
    window.open(`https://wa.me/91${phone}?text=${encodeURIComponent(message)}`, "_blank");
  };

  if (loading) return (
    <div style={styles.center}>
      <p style={{ color: "var(--text-secondary)" }}>Loading...</p>
    </div>
  );

  if (homeServices.length === 0) return (
    <div style={styles.center}>
      <p style={{ color: "var(--text-secondary)" }}>This shop has no home services listed.</p>
      <button onClick={() => navigate(-1)} style={styles.backBtn}>← Go Back</button>
    </div>
  );

  return (
    <div style={styles.page}>
      {step < 4 && (
        <div style={styles.header}>
          <button onClick={() => step === 1 ? navigate(-1) : setStep(step - 1)} style={styles.backLink}>← Back</button>
          <div style={styles.headerInfo}>
            <p style={styles.headerTitle}>🏠 Home Visit Booking</p>
            <p style={styles.headerSub}>{shop?.name}</p>
          </div>
        </div>
      )}

      {step < 4 && (
        <div style={styles.stepBar}>
          {["Services", "Details", "Confirm"].map((label, i) => (
            <div key={i} style={styles.stepItem}>
              <div style={{ ...styles.stepDot, background: step > i ? "#e94560" : step === i + 1 ? "#e94560" : "var(--bg-card)", border: step === i + 1 ? "none" : "1px solid var(--border)" }}>
                <span style={{ color: "white", fontSize: "12px", fontWeight: "bold" }}>{i + 1}</span>
              </div>
              <span style={{ fontSize: "11px", color: step === i + 1 ? "#e94560" : "var(--text-muted)" }}>{label}</span>
            </div>
          ))}
        </div>
      )}

      <div style={styles.container}>

        {/* ── Step 1: Select Services ── */}
        {step === 1 && (
          <div>
            <p style={styles.sectionTitle}>Select home services</p>
            <p style={{ fontSize: "13px", color: "var(--text-muted)", marginBottom: "16px" }}>Tap to select one or more services</p>
            {homeServices.map((service) => {
              const selected = selectedServices.find((s) => s.id === service.id);
              return (
                <div
                  key={service.id}
                  onClick={() => toggleService(service)}
                  style={{ ...styles.serviceCard, border: selected ? "1.5px solid #e94560" : "1px solid var(--border)", background: selected ? "rgba(233,69,96,0.06)" : "var(--bg-card)" }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: "0 0 4px", fontWeight: "500", fontSize: "15px", color: "var(--text-primary)" }}>{service.name}</p>
                      {service.description && <p style={{ margin: "0 0 8px", fontSize: "13px", color: "var(--text-muted)" }}>{service.description}</p>}
                      <div style={{ display: "flex", gap: "12px" }}>
                        <span style={{ fontSize: "13px", color: "var(--text-secondary)" }}>⏱ {service.duration} mins</span>
                        <span style={{ fontSize: "13px", color: "#e94560", fontWeight: "600" }}>₹{service.price}</span>
                      </div>
                      <p style={{ fontSize: "12px", color: "var(--text-muted)", margin: "4px 0 0" }}>Advance: ₹{service.advance_amount}</p>
                    </div>
                    <div style={{ width: "22px", height: "22px", borderRadius: "50%", border: selected ? "none" : "1.5px solid var(--border)", background: selected ? "#e94560" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      {selected && <span style={{ color: "white", fontSize: "13px" }}>✓</span>}
                    </div>
                  </div>
                </div>
              );
            })}

            {selectedServices.length > 0 && (
              <div style={styles.totalBar}>
                <div>
                  <p style={{ margin: 0, fontSize: "13px", color: "var(--text-secondary)" }}>{selectedServices.length} service{selectedServices.length > 1 ? "s" : ""} · {totalDuration} mins</p>
                  <p style={{ margin: "2px 0 0", fontSize: "15px", fontWeight: "600", color: "var(--text-primary)" }}>Total ₹{totalPrice} · Advance ₹{totalAdvance}</p>
                </div>
                <button onClick={() => setStep(2)} style={styles.nextBtn}>Next →</button>
              </div>
            )}
          </div>
        )}

        {/* ── Step 2: Address & Time ── */}
        {step === 2 && (
          <div>
            <p style={styles.sectionTitle}>Your details</p>

            <label style={styles.label}>Home address</label>
            <textarea
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Enter your full address with landmark..."
              rows={3}
              style={styles.textarea}
            />

            <label style={styles.label}>Preferred date</label>
            <input
              type="date"
              value={preferredDate}
              min={getTodayDate()}
              onChange={(e) => setPreferredDate(e.target.value)}
              style={styles.input}
            />

            <label style={styles.label}>Preferred time</label>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "16px" }}>
              {timeSlots.map((slot) => (
                <div
                  key={slot}
                  onClick={() => setPreferredTime(slot)}
                  style={{ ...styles.timeSlot, border: preferredTime === slot ? "1.5px solid #e94560" : "1px solid var(--border)", background: preferredTime === slot ? "rgba(233,69,96,0.06)" : "var(--bg-card)" }}
                >
                  <span style={{ fontSize: "14px", color: preferredTime === slot ? "#e94560" : "var(--text-primary)" }}>{slot}</span>
                  {preferredTime === slot && <span style={{ color: "#e94560" }}>✓</span>}
                </div>
              ))}
            </div>

            <label style={styles.label}>Additional note (optional)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Any special instructions for the barber..."
              rows={2}
              style={styles.textarea}
            />

            <button
              onClick={() => {
                if (!address.trim()) return showToast("Please enter your address.", "warning");
                if (!preferredDate) return showToast("Please select a date.", "warning");
                if (!preferredTime) return showToast("Please select a time slot.", "warning");
                setStep(3);
              }}
              style={styles.nextBtn}
            >
              Next → Confirm
            </button>
          </div>
        )}

        {/* ── Step 3: Confirm & Pay ── */}
        {step === 3 && (
          <div>
            <p style={styles.sectionTitle}>Confirm your booking</p>

            <div style={styles.summaryCard}>
              <p style={styles.summaryHeading}>🏠 Home visit at {shop?.name}</p>

              <div style={styles.summaryRow}>
                <span style={styles.summaryLabel}>Services</span>
                <span style={styles.summaryValue}>{selectedServices.map((s) => s.name).join(", ")}</span>
              </div>
              <div style={styles.summaryRow}>
                <span style={styles.summaryLabel}>Duration</span>
                <span style={styles.summaryValue}>{totalDuration} mins</span>
              </div>
              <div style={styles.summaryRow}>
                <span style={styles.summaryLabel}>Date</span>
                <span style={styles.summaryValue}>{preferredDate}</span>
              </div>
              <div style={styles.summaryRow}>
                <span style={styles.summaryLabel}>Time</span>
                <span style={styles.summaryValue}>{preferredTime}</span>
              </div>
              <div style={styles.summaryRow}>
                <span style={styles.summaryLabel}>Address</span>
                <span style={styles.summaryValue}>{address}</span>
              </div>
              {note && (
                <div style={styles.summaryRow}>
                  <span style={styles.summaryLabel}>Note</span>
                  <span style={styles.summaryValue}>{note}</span>
                </div>
              )}
              <div style={{ borderTop: "1px solid var(--border)", marginTop: "12px", paddingTop: "12px" }}>
                <div style={styles.summaryRow}>
                  <span style={styles.summaryLabel}>Total amount</span>
                  <span style={{ ...styles.summaryValue, fontWeight: "600" }}>₹{totalPrice}</span>
                </div>
                <div style={styles.summaryRow}>
                  <span style={styles.summaryLabel}>Pay at doorstep</span>
                  <span style={{ ...styles.summaryValue, color: "var(--text-muted)" }}>₹{totalPrice - totalAdvance}</span>
                </div>
                <div style={styles.summaryRow}>
                  <span style={styles.summaryLabel}>Advance now</span>
                  <span style={{ ...styles.summaryValue, color: "#e94560", fontWeight: "600" }}>₹{totalAdvance}</span>
                </div>
              </div>
            </div>

            <div style={styles.noteBox}>
              <p style={{ margin: 0, fontSize: "13px", color: "var(--text-secondary)" }}>
                💡 ₹{totalAdvance} advance confirms your booking. The barber will contact you on WhatsApp to confirm the time. Remaining ₹{totalPrice - totalAdvance} is paid at your doorstep after service.
              </p>
            </div>

            <button onClick={handlePayment} disabled={paying} style={styles.payBtn}>
              {paying ? "Processing..." : `Pay ₹${totalAdvance} Advance & Confirm`}
            </button>
          </div>
        )}

        {/* ── Step 4: Success ── */}
        {step === 4 && booking && (
          <div style={styles.successPage}>
            <div style={styles.successIcon}>🎉</div>
            <p style={styles.successTitle}>Home visit booked!</p>
            <p style={styles.successSub}>₹{totalAdvance} advance paid. The barber will confirm your visit on WhatsApp.</p>

            <div style={styles.summaryCard}>
              <div style={styles.summaryRow}>
                <span style={styles.summaryLabel}>Salon</span>
                <span style={styles.summaryValue}>{shop?.name}</span>
              </div>
              <div style={styles.summaryRow}>
                <span style={styles.summaryLabel}>Services</span>
                <span style={styles.summaryValue}>{booking.service_names}</span>
              </div>
              <div style={styles.summaryRow}>
                <span style={styles.summaryLabel}>Date</span>
                <span style={styles.summaryValue}>{booking.preferred_date}</span>
              </div>
              <div style={styles.summaryRow}>
                <span style={styles.summaryLabel}>Time</span>
                <span style={styles.summaryValue}>{booking.preferred_time}</span>
              </div>
              <div style={styles.summaryRow}>
                <span style={styles.summaryLabel}>Address</span>
                <span style={styles.summaryValue}>{booking.customer_address}</span>
              </div>
              <div style={styles.summaryRow}>
                <span style={styles.summaryLabel}>Advance paid</span>
                <span style={{ ...styles.summaryValue, color: "#4caf50", fontWeight: "600" }}>₹{booking.advance_amount}</span>
              </div>
            </div>

            <button
              onClick={() => handleWhatsApp(false)}
              style={{ ...styles.whatsappBtn, background: waOpened ? "#128c3e" : "#25d366" }}
            >
              💬 {waOpened ? "✅ Salon Notified!" : "Notify Salon on WhatsApp"}
            </button>
            <p style={styles.whatsappNote}>
              {waOpened
                ? "Salon has been notified about your home visit ✅"
                : "⚠️ Please notify — the barber needs your address to prepare!"}
            </p>

            <button onClick={() => navigate("/my-bookings")} style={styles.viewBookingsBtn}>
              View My Bookings
            </button>
          </div>
        )}

      </div>

      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: "" })} />
    </div>
  );
}

const styles = {
  page: { minHeight: "100vh", background: "var(--bg-primary)", paddingBottom: "40px" },
  center: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh", gap: "16px" },
  header: { background: "var(--bg-secondary)", padding: "14px 16px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: "12px" },
  backLink: { background: "none", border: "none", color: "#e94560", cursor: "pointer", fontSize: "14px", padding: 0, flexShrink: 0 },
  headerInfo: {},
  headerTitle: { margin: 0, fontSize: "15px", fontWeight: "600", color: "var(--text-primary)" },
  headerSub: { margin: "2px 0 0", fontSize: "12px", color: "var(--text-muted)" },
  stepBar: { display: "flex", justifyContent: "center", gap: "32px", padding: "16px", background: "var(--bg-secondary)", borderBottom: "1px solid var(--border)" },
  stepItem: { display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" },
  stepDot: { width: "24px", height: "24px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" },
  container: { maxWidth: "600px", margin: "0 auto", padding: "20px 16px" },
  sectionTitle: { fontSize: "17px", fontWeight: "600", color: "var(--text-primary)", margin: "0 0 16px" },
  serviceCard: { borderRadius: "12px", padding: "14px", marginBottom: "10px", cursor: "pointer", transition: "all 0.2s" },
  totalBar: { position: "sticky", bottom: 0, background: "var(--bg-secondary)", borderTop: "1px solid var(--border)", padding: "14px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "16px", borderRadius: "12px" },
  nextBtn: { background: "#e94560", color: "white", border: "none", padding: "12px 24px", borderRadius: "10px", cursor: "pointer", fontWeight: "600", fontSize: "14px" },
  label: { display: "block", fontSize: "13px", fontWeight: "500", color: "var(--text-secondary)", marginBottom: "6px" },
  input: { width: "100%", padding: "12px", borderRadius: "10px", fontSize: "15px", marginBottom: "16px", boxSizing: "border-box", colorScheme: "dark", background: "var(--bg-card)", border: "1px solid var(--border)", color: "var(--text-primary)" },
  textarea: { width: "100%", padding: "12px", borderRadius: "10px", fontSize: "14px", marginBottom: "16px", boxSizing: "border-box", resize: "vertical", background: "var(--bg-card)", border: "1px solid var(--border)", color: "var(--text-primary)", fontFamily: "inherit" },
  timeSlot: { padding: "12px 16px", borderRadius: "10px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", transition: "all 0.2s" },
  summaryCard: { background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "12px", padding: "16px", marginBottom: "16px" },
  summaryHeading: { fontWeight: "600", fontSize: "15px", color: "var(--text-primary)", margin: "0 0 12px" },
  summaryRow: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12px", marginBottom: "8px" },
  summaryLabel: { fontSize: "13px", color: "var(--text-muted)", flexShrink: 0 },
  summaryValue: { fontSize: "13px", color: "var(--text-primary)", textAlign: "right" },
  noteBox: { background: "rgba(233,69,96,0.06)", border: "1px solid rgba(233,69,96,0.2)", borderRadius: "10px", padding: "12px", marginBottom: "16px" },
  payBtn: { width: "100%", padding: "14px", background: "#e94560", color: "white", border: "none", borderRadius: "12px", fontSize: "15px", fontWeight: "600", cursor: "pointer" },
  successPage: { textAlign: "center", paddingTop: "20px" },
  successIcon: { fontSize: "56px", marginBottom: "12px" },
  successTitle: { fontSize: "22px", fontWeight: "700", color: "var(--text-primary)", margin: "0 0 8px" },
  successSub: { fontSize: "14px", color: "var(--text-secondary)", marginBottom: "24px", lineHeight: "1.5" },
  whatsappBtn: { width: "100%", padding: "14px", color: "white", border: "none", borderRadius: "12px", fontSize: "15px", fontWeight: "600", cursor: "pointer", marginBottom: "8px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" },
  whatsappNote: { fontSize: "12px", color: "var(--text-muted)", marginBottom: "16px" },
  viewBookingsBtn: { width: "100%", padding: "12px", background: "transparent", color: "var(--text-secondary)", border: "1px solid var(--border)", borderRadius: "12px", fontSize: "14px", cursor: "pointer" },
  backBtn: { background: "#e94560", color: "white", border: "none", padding: "10px 20px", borderRadius: "8px", cursor: "pointer" },
};